import React, {
	memo, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GreenroomState } from '../../stores/types/types';
import { setPptxDeckAnimationsInfo } from '../../stores/actions/pptx-deck-animations';
import {
	PptxDeckPosition, PptxDeckSlideInfo, pptxDeckLinearIndex, pptxDeckNextTarget,
	pptxDeckSlideHasBuilds,
} from '../../utils/pptx-deck-animations-reconcile';

// Stable empty-array reference so the selector fallback doesn't create a new
// array identity on every render (which would re-fire the reconcile effect).
const EMPTY_SLIDES: PptxDeckSlideInfo[] = [];

// Timeout (ms) for a command with no echo. The viewer acks every accepted
// command in ~100ms regardless of animation length (measured), so silence
// means it was dropped (sent mid-animation). A lost NextStep escalates to an
// absolute jump; a silent jump is re-sent.
const STEP_TIMEOUT_MS = 1000;
// Silent JumpToSlides re-sent before concluding the viewer is already there (a
// same-slide GotoSlide echoes nothing, since echoes fire only on change).
const MAX_JUMP_RESENDS = 3;
// Delay (ms) between a load's first viewer report and the first catch-up
// command. On load the engine schedules its own Next() 50ms out; a command
// inside that window stacks on it (measured: an immediate JumpToStep overshot
// onto the next slide, while 250ms+ landed correctly every time).
const LOAD_SETTLE_MS = 300;

interface Props {
	/** Base viewer URL of the converted pptx presentation (its `contentUrl`). */
	contentUrl: string;
	/** Media id — keys AllInfo in PptxDeckAnimationsReducer and (implicitly) this deck. */
	mediaId: number;
	/** 1-indexed current slide (synced source of truth). */
	currentPage: number;
	/** 1-indexed animation step within the slide (synced source of truth). */
	currentStep?: number;
	/** Called once the iframe document has loaded, to match the img/video loaded-count flow. */
	onLoad?: () => void;
	/**
	 * When a canvas drag is in progress, the iframe must stop capturing pointer
	 * events so native `dragover` reaches the parent `.media` drop area (iframes
	 * are separate browsing contexts and otherwise swallow the drag).
	 */
	globalIsDragging?: boolean;
	/**
	 * Called when the viewer reports a step change we never asked for (e.g. a
	 * PPTX build set to play automatically rather than on click). The caller
	 * should persist it as the new synced position so every client converges
	 * on it instead of the reconciler fighting to revert it.
	 */
	onAutoAdvance?: (pos: PptxDeckPosition) => void;
}

/**
 * Renders a pptx as an iframe and reconciles it toward the synced (slide,
 * step) target. A single forward build is driven with NextStep so its
 * animation plays; every other move — back-nav, bursts, cross-slide
 * corrections, late-join catch-up — uses the viewer's absolute JumpToSlide /
 * JumpToStep commands, which are idempotent and need no remount. Identical
 * logic runs on every client, so producer / presenter / broadcast stay in
 * lockstep.
 */
export default memo(({
	contentUrl, mediaId, currentPage, currentStep, onLoad, globalIsDragging, onAutoAdvance,
}: Props) => {
	const dispatch = useDispatch();
	const iframeRef = useRef<HTMLIFrameElement>(null);

	const slides = useSelector(
		(s: GreenroomState) => s.PptxDeckAnimationsReducer.info[mediaId]?.slides,
	) ?? EMPTY_SLIDES;

	// The slide the document loads at. Runtime navigation is postMessage-driven
	// (JumpToSlide/JumpToStep/PreviousStep).
	const [loadSlide] = useState(currentPage);
	// The step the document loads at. The current viewer reads `animation` only
	// on hashchange, so this is ignored on load until the player honors it there;
	// the entry-report catch-up below covers both cases.
	const [loadStep] = useState(currentStep ?? 1);

	// The stored contentUrl already carries a `#slideNumber=1` fragment; strip any
	// existing hash before applying the load slide so we don't stack fragments.
	// `animation` uses the same offset as JumpToStep's StepIndex (our step - 1);
	// steps 1-2 are the entry state, so they need no param.
	const src = useMemo(() => {
		const base = contentUrl.split('#')[0];
		return loadStep > 2
			? `${base}#slideNumber=${loadSlide}&animation=${loadStep - 1}`
			: `${base}#slideNumber=${loadSlide}`;
	}, [contentUrl, loadSlide, loadStep]);

	// Where the iframe actually is, updated from echo messages.
	const appliedRef = useRef<PptxDeckPosition>({ slide: currentPage, step: currentStep ?? 1 });
	// Latest target/slides, mirrored into refs so event handlers avoid stale closures.
	const targetRef = useRef<PptxDeckPosition>({ slide: currentPage, step: currentStep ?? 1 });
	const slidesRef = useRef(slides);
	const inFlightRef = useRef(false);
	// Which kind of command is in flight: a relative NextStep/PreviousStep or an
	// absolute jump.
	const inFlightKindRef = useRef<'step' | 'prev' | 'jump' | null>(null);
	const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
	// After a NextStep times out (dropped), or after any in-flight command is
	// preempted (it may still land, so `applied` is unreliable), drive the very
	// next move with an absolute jump: a relative NextStep could double-advance.
	const escalateRef = useRef(false);
	// Where the in-flight jump should land, and how many times it has been
	// re-sent without an echo. A silent jump is re-sent (idempotent, so a
	// re-send can never overshoot — the viewer may have dropped it while a
	// build was animating); only after repeated silence is it assumed landed,
	// since a jump to the position the viewer is already at echoes nothing.
	const jumpSentRef = useRef<PptxDeckPosition | null>(null);
	const jumpResendsRef = useRef(0);
	// True while the synced target is a same-slide reset to step 1 (the back
	// arrow on slide 1): step back to the pre-animation state instead of settling
	// at the entry state. PreviousSteps snap without animating and are not
	// dropped back-to-back (measured), so the rewind shows no replay.
	const rewindRef = useRef(false);
	// Render-fresh pointer so step()/persist helpers avoid stale closures and
	// identity churn from the prop changing.
	const onAutoAdvanceRef = useRef(onAutoAdvance);
	// An auto-advance we've locally accepted but haven't yet seen echoed back
	// through the `currentPage`/`currentStep` props (the sync round-trip). Until
	// then, keep using it as the target so we don't fight our own accepted
	// position against the stale prop value.
	const pendingAutoAdvanceRef = useRef<PptxDeckPosition | null>(null);
	const lastPropTargetRef = useRef<PptxDeckPosition>({
		slide: currentPage, step: currentStep ?? 1,
	});
	// The position a NextStep was sent from, so its echo can be checked for
	// overshoot (see onSettled). Null while a jump is in flight.
	const sentFromRef = useRef<PptxDeckPosition | null>(null);
	// Set on each document load until LOAD_SETTLE_MS after the viewer's first
	// report. Before the report the viewer hasn't placed itself (no current
	// slide); just after it, the engine's own load-time Next() is still pending.
	const awaitingEntryRef = useRef(false);
	const settleTimerRef = useRef<ReturnType<typeof setTimeout>>();

	const propTarget = { slide: currentPage, step: currentStep ?? 1 };
	if (propTarget.slide !== lastPropTargetRef.current.slide
		|| propTarget.step !== lastPropTargetRef.current.step) {
		// The prop moved — either our own correction landed, or real new intent
		// (e.g. a Next click) arrived. Either way, trust the prop over any stale
		// pending override.
		pendingAutoAdvanceRef.current = null;
		rewindRef.current = propTarget.slide === lastPropTargetRef.current.slide
			&& propTarget.step === 1 && lastPropTargetRef.current.step > 1;
		lastPropTargetRef.current = propTarget;
	}
	targetRef.current = pendingAutoAdvanceRef.current ?? propTarget;
	slidesRef.current = slides;
	onAutoAdvanceRef.current = onAutoAdvance;

	const post = useCallback((
		type: 'AllInfo' | 'NextStep' | 'PreviousStep' | 'JumpToSlide' | 'JumpToStep',
		extra?: { SlideNumber?: number; StepIndex?: number },
	) => {
		iframeRef.current?.contentWindow?.postMessage({ Type: type, ...extra }, '*');
	}, []);

	// Accept `pos` as the new baseline and ask the caller to persist it so
	// every client converges on it via the normal sync channel.
	const persistPosition = useCallback((pos: PptxDeckPosition) => {
		pendingAutoAdvanceRef.current = pos;
		targetRef.current = pos;
		onAutoAdvanceRef.current?.(pos);
	}, []);

	// Drive one command toward the target; re-invoked on each echo/timeout.
	// Our step model is one above the viewer's: viewer CurrentStep is 1-indexed
	// (1..TotalSteps) and our step is CurrentStep + 1, so a JumpToStep for our
	// step S asks for the viewer's StepIndex S - 1.
	const step = useCallback(() => {
		if (inFlightRef.current || awaitingEntryRef.current) return;
		const applied = appliedRef.current;
		const target = targetRef.current;
		if (applied.slide === target.slide && applied.step === target.step) return;
		if (rewindRef.current && applied.slide === target.slide && target.step === 1
			&& applied.step > 1 && pptxDeckSlideHasBuilds(slidesRef.current, target.slide)) {
			// One PreviousStep at a time; each echo drives the next.
			inFlightRef.current = true;
			inFlightKindRef.current = 'prev';
			sentFromRef.current = applied;
			post('PreviousStep');
			timeoutRef.current = setTimeout(() => {
				// A landed PreviousStep always echoes, so silence means it was
				// dropped (e.g. mid-animation): re-send a few times, then assume landed.
				inFlightRef.current = false;
				inFlightKindRef.current = null;
				sentFromRef.current = null;
				if (jumpResendsRef.current < MAX_JUMP_RESENDS) {
					jumpResendsRef.current += 1;
				} else {
					appliedRef.current = { slide: applied.slide, step: applied.step - 1 };
					jumpResendsRef.current = 0;
				}
				step();
			}, STEP_TIMEOUT_MS);
			return;
		}
		// A slide's entry state is its real floor — the viewer never reports a
		// step below 2 (our model) once a slide is entered — so a step-1 target
		// is already satisfied at the entry state.
		if (applied.slide === target.slide && applied.step === 2 && target.step === 1) return;
		const escalated = escalateRef.current;
		escalateRef.current = false;
		const next = pptxDeckNextTarget(slidesRef.current, applied.slide, applied.step);
		if (!escalated && next && next.slide === applied.slide
			&& next.slide === target.slide && next.step === target.step) {
			// Exactly one build ahead on the same slide: NextStep, so the
			// animation plays. Slide changes always jump (absolute, droppable
			// NextSteps never carry a page turn).
			inFlightRef.current = true;
			inFlightKindRef.current = 'step';
			sentFromRef.current = applied;
			post('NextStep');
			timeoutRef.current = setTimeout(() => {
				inFlightRef.current = false;
				inFlightKindRef.current = null;
				sentFromRef.current = null;
				escalateRef.current = true;
				step();
			}, STEP_TIMEOUT_MS);
			return;
		}
		if (target.slide !== applied.slide) {
			// Lands at the target slide's entry state (our step 2) and echoes a
			// SlideComplete; a follow-up JumpToStep reaches a deeper target step.
			inFlightRef.current = true;
			inFlightKindRef.current = 'jump';
			sentFromRef.current = null;
			jumpSentRef.current = { slide: target.slide, step: 2 };
			post('JumpToSlide', { SlideNumber: target.slide });
			timeoutRef.current = setTimeout(() => {
				// Silence: the viewer may already be on that slide (same-slide
				// GotoSlide echoes nothing). Re-send a few times (idempotent),
				// then assume landed — a later echo corrects a wrong assumption.
				inFlightRef.current = false;
				inFlightKindRef.current = null;
				if (jumpSentRef.current && jumpResendsRef.current < MAX_JUMP_RESENDS) {
					jumpResendsRef.current += 1;
				} else if (jumpSentRef.current) {
					appliedRef.current = jumpSentRef.current;
					jumpResendsRef.current = 0;
				}
				jumpSentRef.current = null;
				step();
			}, STEP_TIMEOUT_MS);
			return;
		}
		// Same-slide absolute jump. The engine's JumpToAnim ends with its own
		// Next(), so a jump that changes position echoes SlideAnimationComplete
		// (measured ~50ms); it is in flight as a jump, so a wrong landing is
		// corrected by onSettled rather than persisted as an auto-advance.
		// Silence means it was already there (echoes fire only on change). Never
		// re-sent: a JumpToAnim visibly REPLAYS builds (jumpTo.playPrev), so a
		// duplicate would show them twice.
		const dest = { slide: target.slide, step: Math.max(2, target.step) };
		inFlightRef.current = true;
		inFlightKindRef.current = 'jump';
		sentFromRef.current = null;
		jumpSentRef.current = dest;
		post('JumpToStep', { StepIndex: Math.max(1, target.step - 1) });
		timeoutRef.current = setTimeout(() => {
			inFlightRef.current = false;
			inFlightKindRef.current = null;
			if (jumpSentRef.current) appliedRef.current = jumpSentRef.current;
			jumpSentRef.current = null;
			step();
		}, STEP_TIMEOUT_MS);
		// The entry floor satisfies a step-1 target; persist it so the next
		// click advances from the synced position instead of being swallowed.
		if (target.step === 1 && dest.step === 2) persistPosition(dest);
	}, [post, persistPosition]);

	const onSettled = useCallback((pos: PptxDeckPosition) => {
		if (awaitingEntryRef.current && !settleTimerRef.current) {
			settleTimerRef.current = setTimeout(() => {
				settleTimerRef.current = undefined;
				awaitingEntryRef.current = false;
				step();
			}, LOAD_SETTLE_MS);
		}
		const wasInFlight = inFlightRef.current;
		const wasJump = wasInFlight && inFlightKindRef.current === 'jump';
		const sentFrom = sentFromRef.current;
		appliedRef.current = pos;
		if (timeoutRef.current) clearTimeout(timeoutRef.current);
		inFlightRef.current = false;
		inFlightKindRef.current = null;
		sentFromRef.current = null;
		jumpSentRef.current = null;
		jumpResendsRef.current = 0;

		if (wasJump) {
			// The echo answers our absolute jump: enforce the synced intent (a
			// re-entered slide reports its kept state here — step() corrects it
			// back down with a JumpToStep so its builds visibly reset), never
			// classify it as an auto-play. One exception: the entry floor. A
			// step-1 target settles at the slide's entry state (our step 2),
			// which is persisted so clicks keep working from the synced position.
			const target = targetRef.current;
			if (pos.slide === target.slide && target.step === 1 && pos.step === 2
				&& !rewindRef.current) {
				persistPosition(pos);
				return;
			}
			step();
			return;
		}

		// Candidate auto-advances: a fully unsolicited report, or a NextStep
		// echo that landed further than one step (e.g. a build that auto-plays
		// immediately after the one we asked for).
		let autoAdvanced = !(wasInFlight && sentFrom);
		if (wasInFlight && sentFrom) {
			const expectedLinear = pptxDeckLinearIndex(slidesRef.current, sentFrom.slide, sentFrom.step)
				+ 1;
			const actualLinear = pptxDeckLinearIndex(slidesRef.current, pos.slide, pos.step);
			if (actualLinear > expectedLinear) autoAdvanced = true;
		}

		// Persist only a position past the synced target. A report at or behind
		// it (e.g. the entry report a fresh load always emits) is the catch-up
		// baseline, not user intent — persisting it would move the deck for
		// every client just because one client (re)loaded the document.
		if (autoAdvanced) {
			const target = targetRef.current;
			const posLinear = pptxDeckLinearIndex(slidesRef.current, pos.slide, pos.step);
			const targetLinear = pptxDeckLinearIndex(slidesRef.current, target.slide, target.step);
			// A rewind's target sits below the entry report a reload emits; keep
			// rewinding rather than persisting that report.
			if (posLinear > targetLinear && !(rewindRef.current && pos.slide === target.slide)) {
				persistPosition(pos);
				return;
			}
		}
		step();
	}, [step, persistPosition]);

	// Listen for the iframe's messages (filtered to this iframe's window).
	useEffect(() => {
		const onMessage = (e: MessageEvent) => {
			if (e.source !== iframeRef.current?.contentWindow) return;
			const d = e.data;
			if (!d || !d.Type) return;
			switch (d.Type) {
				case 'SlideLoaded':
					post('AllInfo');
					break;
				case 'AllInfo':
					if (Array.isArray(d.Slides)) dispatch(setPptxDeckAnimationsInfo(mediaId, d.Slides));
					break;
				case 'SlideComplete':
				case 'SlideAnimationComplete':
					// The viewer reports CurrentStep 1-indexed (1..TotalSteps, per
					// its custom.player.js). Our (slide, step) model sits one above
					// it — the +1 here and the `totalSteps + 1` step count keep the
					// whole model consistent, with step 1 as a load-time base the
					// viewer itself never reports.
					onSettled({ slide: d.SlideIndex, step: d.CurrentStep + 1 });
					break;
				default:
					break;
			}
		};
		window.addEventListener('message', onMessage);
		return () => window.removeEventListener('message', onMessage);
	}, [mediaId, dispatch, post, onSettled]);

	// Reconcile whenever the target or the known deck structure changes. A
	// command whose destination the target has moved past is preempted
	// immediately: jumps are absolute and postMessages are processed in order,
	// so a fresh jump can be stacked on top of anything still in flight —
	// rapid page changes never wait on a stale echo or the timeout.
	useEffect(() => {
		if (inFlightRef.current) {
			const target = targetRef.current;
			const sentFrom = sentFromRef.current;
			const dest = inFlightKindRef.current === 'jump'
				? jumpSentRef.current
				: sentFrom && pptxDeckNextTarget(slidesRef.current, sentFrom.slide, sentFrom.step);
			const destMatches = !!dest && dest.slide === target.slide
				&& (dest.step === target.step || (target.step === 1 && dest.step === 2));
			// A PreviousStep is still wanted while the target lies below it on
			// the same slide; preempting it would re-send and double-step.
			const prevStillWanted = inFlightKindRef.current === 'prev' && !!sentFrom
				&& target.slide === sentFrom.slide && target.step < sentFrom.step;
			if (!destMatches && !prevStillWanted) {
				if (timeoutRef.current) clearTimeout(timeoutRef.current);
				inFlightRef.current = false;
				inFlightKindRef.current = null;
				sentFromRef.current = null;
				jumpSentRef.current = null;
				escalateRef.current = true;
			}
		}
		step();
	}, [currentPage, currentStep, slides, step]);

	// Clear any pending timers on unmount so nothing fires after teardown.
	useEffect(() => () => {
		if (timeoutRef.current) clearTimeout(timeoutRef.current);
		if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
	}, []);

	// A fresh document load. Catch-up toward the synced target (late joiners /
	// spotlight re-parents at currentStep > 1) is NOT started here: the viewer
	// lands one step past the slide's base (its entry report is CurrentStep 1)
	// and reports that position immediately — stepping from an assumed base
	// would overshoot by one. The entry report establishes the true position
	// and drives the catch-up.
	const handleLoad = useCallback(() => {
		appliedRef.current = { slide: loadSlide, step: 1 };
		if (timeoutRef.current) clearTimeout(timeoutRef.current);
		inFlightRef.current = false;
		inFlightKindRef.current = null;
		escalateRef.current = false;
		jumpSentRef.current = null;
		jumpResendsRef.current = 0;
		awaitingEntryRef.current = true;
		if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
		settleTimerRef.current = undefined;
		post('AllInfo');
		onLoad?.();
	}, [post, onLoad, loadSlide]);

	return (
		<iframe
			ref={iframeRef}
			className="media-iframe"
			src={src}
			title="presentation"
			onLoad={handleLoad}
			style={{ pointerEvents: globalIsDragging ? 'none' : undefined }}
			sandbox="allow-scripts allow-same-origin"
			referrerPolicy="strict-origin-when-cross-origin"
		/>
	);
});
